import {
  CATEGORY_ICONS,
  addDays,
  fmt,
  fmtDate,
  fmtQty,
  movementsBetween,
  stockAsOf,
  todayKey,
  type DayKey,
  type Product,
} from '@k2pos/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { CategoryPill } from '@/components/ui/Chip';
import { Field } from '@/components/ui/Field';
import { Mono, Text } from '@/components/ui/Text';
import { cx } from '@/lib/cx';
import { useGoBack } from '@/hooks/useNav';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Stock.module.css';

const ALL = 'ທັງໝົດ';

/**
 * ສະຕັອກ. The catalog — name, unit, price — is the required baseline; the
 * remaining count is an optional layer, so items without one are not "missing data".
 */
export function Stock() {
  const navigate = useNavigate();
  const goBack = useGoBack('/more');
  const catalog = usePos((s) => s.catalog);
  const retired = usePos((s) => s.retired);
  const [showRetired, setShowRetired] = useState(false);
  const journal = usePos((s) => s.journal);
  const journalStartedAt = usePos((s) => s.journalStartedAt);
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState(ALL);
  const [day, setDay] = useState<DayKey>(() => todayKey());

  const today = todayKey();
  const historical = day !== today;
  // The ledger starts when the app was updated; before that there is nothing to
  // replay, and a plausible-looking number would be worse than saying so.
  const beforeLedger = day < journalStartedAt;

  /**
   * Replayed backwards from today's figure rather than read from a snapshot.
   * Snapshots would need a scheduler the app does not have and a device awake at
   * midnight, and one taken from a wrong count would freeze that error forever.
   * Replay always reconciles to the current truth.
   */
  const asOf = useMemo(
    () => (historical ? stockAsOf(catalog, journal, day) : null),
    [historical, catalog, journal, day],
  );

  const moved = useMemo(
    () =>
      historical
        ? null
        : new Map(
            catalog.map((p) => [
              p.id,
              movementsBetween(journal, p.id, { from: today, to: today }),
            ]),
          ),
    [historical, catalog, journal, today],
  );

  const categories = useMemo(() => {
    const counts = new Map<string, number>([[ALL, catalog.length]]);
    for (const p of catalog) counts.set(p.cat, (counts.get(p.cat) ?? 0) + 1);
    return [...counts.entries()];
  }, [catalog]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Retired products are a separate list from the server, so they are absent
    // here unless asked for — the same reason Odoo filters `active` in the ORM
    // rather than in each view.
    const list = showRetired ? [...catalog, ...retired] : catalog;
    return list.filter(
      (p) => (cat === ALL || p.cat === cat) && (!q || p.name.toLowerCase().includes(q)),
    );
  }, [catalog, retired, showRetired, cat, query]);

  const lowCount = catalog.filter(isLow).length;

  const DAY_CHIPS: Array<{ key: string; label: string; day: DayKey }> = [
    { key: 'today', label: 'ມື້ນີ້', day: today },
    { key: 'yesterday', label: 'ມື້ວານ', day: addDays(today, -1) },
  ];

  return (
    <>
      <AppBar
        title="ສະຕັອກ"
        onBack={goBack}
        subtitle={
          lowCount > 0 ? (
            <Text size={11} color={colors.sky}>
              {lowCount} ລາຍການເຫຼືອໜ້ອຍ
            </Text>
          ) : undefined
        }
        right={
          <button type="button" onClick={() => navigate('/add-product')}>
            <Text weight={700} size={13} color={colors.sky}>
              + ເພີ່ມ
            </Text>
          </button>
        }
      />

      <div className={styles.controls}>
        <Field
          value={query}
          onChange={setQuery}
          placeholder="ຄົ້ນຫາສິນຄ້າ..."
          label="ຄົ້ນຫາສິນຄ້າ"
        />

        <div className={styles.dayRow}>
          {DAY_CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setDay(c.day)}
              className={cx(styles.dayChip, c.day === day && styles.dayChipOn)}
            >
              <Text
                weight={c.day === day ? 800 : 500}
                size={11.5}
                color={c.day === day ? colors.surfaceAlt : colors.textMuted}
              >
                {c.label}
              </Text>
            </button>
          ))}
          <input
            type="date"
            value={day}
            max={today}
            // 90 days keeps the backward replay bounded; beyond that a shop
            // wants a report, not a stock screen.
            min={addDays(today, -90)}
            onChange={(e) => setDay(e.target.value || today)}
            aria-label="ເລືອກວັນທີ"
            className={styles.dayInput}
          />
        </div>
      </div>

      {/* Loud on purpose: mistaking a past reading for current stock produces a
          wrong reorder, which costs real money. */}
      {historical ? (
        <button type="button" onClick={() => setDay(today)} className={styles.pastBanner}>
          <div className="grow col">
            <Text weight={800} size={12} color={colors.navy} align="left">
              ກຳລັງເບິ່ງຍ້ອນຫຼັງ · {fmtDate(new Date(`${day}T00:00:00`))}
            </Text>
            <Text size={10.5} color={colors.textMuted} align="left">
              ບໍ່ແມ່ນສະຕັອກປັດຈຸບັນ · ກົດເພື່ອກັບມື້ນີ້
            </Text>
          </div>
        </button>
      ) : null}

      {/* .hscroll keeps the pill row from absorbing the column's leftover height. */}
      <div className={cx('hscroll', styles.pills)} role="tablist">
        {categories.map(([name, count]) => (
          <CategoryPill
            key={name}
            name={name}
            icon={CATEGORY_ICONS[name] ?? 'grid'}
            count={count}
            selected={cat === name}
            onPress={() => setCat(name)}
          />
        ))}
      </div>

      {retired.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowRetired((v) => !v)}
          className={styles.retiredToggle}
        >
          <Text size={11} weight={700} color={colors.navy}>
            {showRetired
              ? 'ເຊື່ອງທີ່ເລີກຂາຍ'
              : `ສະແດງທີ່ເລີກຂາຍ (${retired.length})`}
          </Text>
        </button>
      ) : null}

      <div className={cx('vscroll', styles.list)}>
        {visible.map((product) => {
          const unit = product.units[0]!;
          const low = !historical && isLow(product);
          const shown = historical ? asOf?.get(product.id) : product.remaining;
          const move = moved?.get(product.id);

          return (
            <button
              key={product.id}
              type="button"
              onClick={() => navigate(`/product/${product.id}`)}
              className={styles.rowBtn}
            >
            <Card accent={low ? colors.sky : undefined} className={styles.row}>
              <div className="grow col">
                <Text weight={700} size={14} lines={1}>
                  {product.name}
                </Text>
                <Mono size={11} color={colors.textMuted} lines={1}>
                  ໜ່ວຍ: {unit.unit} · {fmt(unit.price)} ₭
                </Mono>
              </div>

              <div className={styles.rowRight}>
                {beforeLedger ? (
                  <Text size={10.5} color={colors.textFaint} align="right">
                    ບໍ່ມີຂໍ້ມູນ
                  </Text>
                ) : shown === undefined ? (
                  <Text size={11} color={colors.textFaint}>
                    ບໍ່ນັບສະຕັອກ
                  </Text>
                ) : (
                  <Mono weight={700} size={15} color={low ? colors.skyDeep : colors.text}>
                    {fmtQty(shown)}
                  </Mono>
                )}
                {/* What "stock by date" is actually for: not the number alone,
                    but what moved to get there. */}
                {move && (move.sold > 0 || move.received > 0) ? (
                  <Mono size={9.5} color={colors.textFaint}>
                    {move.sold > 0 ? `ຂາຍ ${fmtQty(move.sold)}` : ''}
                    {move.sold > 0 && move.received > 0 ? ' · ' : ''}
                    {move.received > 0 ? `ຮັບເຂົ້າ ${fmtQty(move.received)}` : ''}
                  </Mono>
                ) : null}
                {low ? (
                  <span className={styles.lowFlag}>
                    <Text weight={800} size={9.5} color={colors.skyDeep}>
                      ໜ້ອຍ!
                    </Text>
                  </span>
                ) : null}
              </div>
            </Card>
            </button>
          );
        })}
      </div>
    </>
  );
}

function isLow(product: Product): boolean {
  return (
    product.remaining !== undefined &&
    product.lowStockAt !== undefined &&
    product.remaining <= product.lowStockAt
  );
}
